class PressLogosMarquee {
  constructor(container) {
    this.container = container;
    this.marquee = container.querySelector('.press-logos__marquee');
    this.mobileBreakpoint = 989;
    this.duration = parseFloat(this.marquee?.dataset.speed) || 20;
    this.pixelsPerSecond = 0;
    this.position = 0;
    this.isRunning = false;
    this.animationId = null;
    this.items = [];
    this.gap = 40;

    this.init();
  }

  init() {
    if (!this.marquee) return;
    this.checkViewport();
    this.handleResize = this.debounce(this.checkViewport.bind(this), 150);
    window.addEventListener('resize', this.handleResize);
  }

  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }

  checkViewport() {
    const isMobile = window.innerWidth <= this.mobileBreakpoint;

    if (isMobile && !this.isRunning) {
      this.startMarquee();
    } else if (!isMobile && this.isRunning) {
      this.stopMarquee();
    }
  }

  startMarquee() {
    if (this.isRunning) return;
    this.isRunning = true;

    // Clone items until we have enough to fill screen + buffer
    this.cloneItems();

    // Start animation
    this.lastTime = performance.now();
    this.animate();
  }

  cloneItems() {
    const originalItems = Array.from(this.marquee.querySelectorAll('.press-logos__item:not(.press-logos__item--clone)'));
    if (originalItems.length === 0) return;

    // Calculate how many clones we need
    const containerWidth = this.container.offsetWidth;
    let totalWidth = 0;

    originalItems.forEach(item => {
      totalWidth += item.offsetWidth + this.gap;
    });

    // We need at least 3x the content to ensure smooth infinite scroll
    const clonesNeeded = Math.ceil((containerWidth * 3) / totalWidth);

    for (let i = 0; i < clonesNeeded; i++) {
      originalItems.forEach(item => {
        const clone = item.cloneNode(true);
        clone.classList.add('press-logos__item--clone');
        clone.setAttribute('aria-hidden', 'true');
        this.marquee.appendChild(clone);
      });
    }

    // Store all items
    this.items = Array.from(this.marquee.querySelectorAll('.press-logos__item'));
    this.singleSetWidth = totalWidth;

    // Calculate speed based on duration setting
    // duration = time to scroll one complete set of items
    this.pixelsPerSecond = this.singleSetWidth / this.duration;
  }

  animate() {
    if (!this.isRunning) return;

    const now = performance.now();
    const delta = (now - this.lastTime) / 1000;
    this.lastTime = now;

    this.position -= this.pixelsPerSecond * delta;

    // Reset position when we've scrolled one full set
    if (Math.abs(this.position) >= this.singleSetWidth) {
      this.position += this.singleSetWidth;
    }

    this.marquee.style.transform = `translateX(${this.position}px)`;

    this.animationId = requestAnimationFrame(() => this.animate());
  }

  stopMarquee() {
    this.isRunning = false;

    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }

    // Remove clones
    const clones = this.marquee.querySelectorAll('.press-logos__item--clone');
    clones.forEach(clone => clone.remove());

    // Reset position
    this.position = 0;
    this.marquee.style.transform = '';
  }

  destroy() {
    window.removeEventListener('resize', this.handleResize);
    this.stopMarquee();
  }
}

if (typeof window !== 'undefined') {
  const initPressLogos = () => {
    const sections = document.querySelectorAll('[data-section-type="press-logos"]');
    sections.forEach(section => {
      if (!section.pressLogosInstance) {
        section.pressLogosInstance = new PressLogosMarquee(section);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPressLogos);
  } else {
    initPressLogos();
  }

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      const section = event.target.querySelector('[data-section-type="press-logos"]');
      if (section) {
        section.pressLogosInstance = new PressLogosMarquee(section);
      }
    });

    document.addEventListener('shopify:section:unload', (event) => {
      const section = event.target.querySelector('[data-section-type="press-logos"]');
      if (section && section.pressLogosInstance) {
        section.pressLogosInstance.destroy();
      }
    });
  }
}
